// =====================================================
// BED — Modèle du wizard multi-étapes (logique PURE, testable)
// =====================================================
// Ce module ne connaît ni React, ni le réseau : il transforme les champs du
// formulaire BED + les valeurs saisies en tout ce dont l'UI a besoin (étapes,
// visibilité conditionnelle, exigences, état de la frise, liste des éléments
// manquants). Le serveur (`bed_compute_missing_items`, 00177) reste la SOURCE
// DE VÉRITÉ des éléments manquants persistés ; ce miroir client sert à donner
// un retour immédiat entre deux enregistrements, avec exactement les mêmes
// règles.

import type { BedDeferrableGroup, FormFieldWithValues, TargetRole } from '../../types/form';

// -----------------------------------------------------------------------
// ÉTAPES
// -----------------------------------------------------------------------

/** Identifiant d'étape = préfixe de `form_fields.order_index` (étape * 1000). */
export type BedStepId = 1 | 2 | 3 | 4 | 5 | 6;

export interface BedStep {
  id: BedStepId;
  /** Numéro affiché dans la frise. */
  index: number;
  title: string;
  /** Sous-titre court (mobile-first : on reste très bref). */
  hint: string;
}

export const BED_STEPS: readonly BedStep[] = [
  { id: 1, index: 1, title: 'Équipage', hint: 'Nom et numéro' },
  { id: 2, index: 2, title: 'Pilote', hint: 'Identité et documents' },
  { id: 3, index: 3, title: 'Copilote', hint: 'Identité et documents' },
  { id: 4, index: 4, title: 'Véhicule', hint: 'Immatriculation' },
  { id: 5, index: 5, title: 'Options', hint: 'Assurances et consentements' },
  { id: 6, index: 6, title: 'Récapitulatif', hint: 'Signature et paiement' },
] as const;

export const BED_LAST_STEP: BedStepId = 6;

/**
 * Étape d'un champ, dérivée de son `order_index` (convention 00177 :
 * `order_index = étape * 1000 + slot`). Un champ hors plage retombe sur
 * l'étape ① : il reste saisissable, jamais perdu.
 */
export function stepOfField(field: Pick<FormFieldWithValues, 'order_index'>): BedStepId {
  const step = Math.floor(field.order_index / 1000);
  return step >= 1 && step <= BED_LAST_STEP ? (step as BedStepId) : 1;
}

/** Regroupe les champs par étape, en conservant l'ordre `order_index`. */
export function groupFieldsByStep(
  fields: FormFieldWithValues[]
): Record<BedStepId, FormFieldWithValues[]> {
  const out = { 1: [], 2: [], 3: [], 4: [], 5: [], 6: [] } as Record<
    BedStepId,
    FormFieldWithValues[]
  >;
  [...fields]
    .sort((a, b) => a.order_index - b.order_index)
    .forEach((field) => out[stepOfField(field)].push(field));
  return out;
}

// -----------------------------------------------------------------------
// GROUPES REPORTABLES (« je m'engage à communiquer avant le … »)
// -----------------------------------------------------------------------

/**
 * Clés du dictionnaire de reports du brouillon : `<groupe>:<rôle>`.
 * `certificat` s'ajoute aux groupes SQL : c'est un upload, pas un champ, mais
 * il se reporte de la même façon (cahier L120-121).
 */
export type BedDeferralGroup = BedDeferrableGroup | 'certificat';

export const BED_DEFERRAL_GROUP_LABELS: Record<BedDeferralGroup, string> = {
  passport: 'Passeport',
  permis: 'Permis de conduire',
  ceam: 'Carte européenne d’assurance maladie',
  certificat: 'Certificat médical',
};

export const BED_ROLE_LABELS: Record<string, string> = {
  pilot: 'Pilote',
  copilot: 'Copilote',
  common: 'Équipage',
};

/** Rôle normalisé d'un champ (`null` → `'common'`), pour indexer les maps. */
export function roleKeyOf(role: TargetRole | undefined): string {
  return role ?? 'common';
}

/** Clé de report d'un groupe pour un rôle donné. */
export function deferralKey(group: BedDeferralGroup, role: string): string {
  return `${group}:${role}`;
}

/** Report déclaré : la date à laquelle le participant s'engage à fournir. */
export interface BedDeferral {
  due_date: string;
}

export type BedDeferralMap = Record<string, BedDeferral>;

// -----------------------------------------------------------------------
// VALEURS
// -----------------------------------------------------------------------

export type BedValues = Record<string, string>;

/** Valeurs stockées pour les cases à cocher (compatibles trigger SQL). */
export const BED_CHECKBOX_TRUE = 'true';
export const BED_CHECKBOX_FALSE = '';

/**
 * Valeurs du champ `is_not_student` — reprises TELLES QUELLES du socle : le
 * trigger `create_contact_from_submission` reconnaît 'Jeune Actif' comme
 * « n'est plus étudiant ». Ne pas les remplacer par des booléens.
 */
export const STUDENT_TRUE_VALUE = 'Jeune Actif';
export const STUDENT_FALSE_VALUE = 'Étudiant';

export function isChecked(values: BedValues, fieldId: string | undefined): boolean {
  if (!fieldId) return false;
  const raw = values[fieldId];
  return raw === BED_CHECKBOX_TRUE || raw === 'on' || raw === '1';
}

/**
 * « N'est plus étudiant(e) » — le champ est textuel ('Jeune Actif' /
 * 'Étudiant', contrat du trigger SQL). Tolère aussi les booléens sérialisés
 * qu'un pré-remplissage ancien pourrait avoir laissés.
 */
export function isNotStudent(values: BedValues, fieldId: string | undefined): boolean {
  if (!fieldId) return false;
  const raw = values[fieldId];
  return raw === STUDENT_TRUE_VALUE || raw === 'true' || raw === '1';
}

function isFilled(values: BedValues, fieldId: string): boolean {
  return String(values[fieldId] ?? '').trim() !== '';
}

// -----------------------------------------------------------------------
// INDEX DES CHAMPS PAR RÔLE
// -----------------------------------------------------------------------

/**
 * Repères dont les règles conditionnelles ont besoin. Les champs sans
 * `contact_field_map` sont identifiés par leur `order_index` : la convention
 * 00177 fixe les slots (x410 = « pas de CEAM », x500 = « déjà participé »,
 * x510 = édition, x7xx = adresse de réception du courrier).
 */
const SLOT_NO_CEAM = 410;
const SLOT_ALREADY_PARTICIPATED = 500;
const SLOT_PREVIOUS_EDITION = 510;
const SLOT_MAIL_ADDRESS_MIN = 700;
const SLOT_MAIL_ADDRESS_MAX = 799;

function slotOf(field: FormFieldWithValues): number {
  return field.order_index % 1000;
}

/** Un champ appartient-il au bloc « adresse de réception du courrier » ? */
export function isMailAddressField(field: FormFieldWithValues): boolean {
  const slot = slotOf(field);
  return slot >= SLOT_MAIL_ADDRESS_MIN && slot <= SLOT_MAIL_ADDRESS_MAX;
}

export interface BedFieldIndex {
  /** `is_not_student` par rôle. */
  student: Record<string, FormFieldWithValues>;
  school: Record<string, FormFieldWithValues>;
  schoolCity: Record<string, FormFieldWithValues>;
  profession: Record<string, FormFieldWithValues>;
  noCeam: Record<string, FormFieldWithValues>;
  ceamNumber: Record<string, FormFieldWithValues>;
  alreadyParticipated: Record<string, FormFieldWithValues>;
  previousEdition: Record<string, FormFieldWithValues>;
  mailAddress: Record<string, FormFieldWithValues[]>;
}

export function buildFieldIndex(fields: FormFieldWithValues[]): BedFieldIndex {
  const index: BedFieldIndex = {
    student: {},
    school: {},
    schoolCity: {},
    profession: {},
    noCeam: {},
    ceamNumber: {},
    alreadyParticipated: {},
    previousEdition: {},
    mailAddress: {},
  };

  for (const field of fields) {
    const role = roleKeyOf(field.target_role);
    const slot = slotOf(field);

    switch (field.contact_field_map) {
      case 'is_not_student':
        index.student[role] = field;
        continue;
      case 'school':
        index.school[role] = field;
        continue;
      case 'school_city':
        index.schoolCity[role] = field;
        continue;
      case 'socio_professional_category':
        index.profession[role] = field;
        continue;
      default:
        break;
    }

    if (field.bed_deferrable_group === 'ceam') {
      index.ceamNumber[role] = field;
    }
    if (slot === SLOT_NO_CEAM) index.noCeam[role] = field;
    if (slot === SLOT_ALREADY_PARTICIPATED) index.alreadyParticipated[role] = field;
    if (slot === SLOT_PREVIOUS_EDITION) index.previousEdition[role] = field;
    if (isMailAddressField(field)) {
      (index.mailAddress[role] ??= []).push(field);
    }
  }

  return index;
}

// -----------------------------------------------------------------------
// RÔLES ACTIFS
// -----------------------------------------------------------------------

/**
 * Un rôle est « actif » dès qu'au moins un de ses champs porte une valeur.
 * Miroir EXACT de la règle serveur (`bed_compute_missing_items`) : un équipage
 * sans copilote ne se voit pas reprocher trente champs fantômes.
 */
export function activeRoles(fields: FormFieldWithValues[], values: BedValues): Set<string> {
  const active = new Set<string>(['common']);
  for (const field of fields) {
    if (isFilled(values, field.id)) active.add(roleKeyOf(field.target_role));
  }
  return active;
}

// -----------------------------------------------------------------------
// VISIBILITÉ ET EXIGENCE CONDITIONNELLES
// -----------------------------------------------------------------------

export interface BedRuleContext {
  index: BedFieldIndex;
  values: BedValues;
  deferred: BedDeferralMap;
  active: Set<string>;
}

export function buildRuleContext(
  fields: FormFieldWithValues[],
  values: BedValues,
  deferred: BedDeferralMap
): BedRuleContext {
  return {
    index: buildFieldIndex(fields),
    values,
    deferred,
    active: activeRoles(fields, values),
  };
}

/** Le participant a-t-il reporté ce groupe pour ce rôle ? */
export function isGroupDeferred(
  ctx: Pick<BedRuleContext, 'deferred'>,
  group: BedDeferralGroup,
  role: string
): boolean {
  return Boolean(ctx.deferred[deferralKey(group, role)]);
}

/**
 * Visibilité d'un champ. Règles du cahier :
 *  - « je ne suis plus étudiant(e) » coché → école ET ville d'école masquées ;
 *  - « mon pays ne fournit pas de CEAM » coché → numéro CEAM masqué ;
 *  - « a déjà participé » décoché → champ « quelle édition » masqué.
 */
export function isFieldVisible(field: FormFieldWithValues, ctx: BedRuleContext): boolean {
  const role = roleKeyOf(field.target_role);
  const notStudent = isNotStudent(ctx.values, ctx.index.student[role]?.id);

  if (
    (field.contact_field_map === 'school' || field.contact_field_map === 'school_city') &&
    notStudent
  ) {
    return false;
  }

  if (field.bed_deferrable_group === 'ceam' && isChecked(ctx.values, ctx.index.noCeam[role]?.id)) {
    return false;
  }

  if (
    ctx.index.previousEdition[role]?.id === field.id &&
    !isChecked(ctx.values, ctx.index.alreadyParticipated[role]?.id)
  ) {
    return false;
  }

  return true;
}

/**
 * Exigence effective d'un champ, une fois les règles conditionnelles appliquées.
 * Un champ invisible n'est jamais requis.
 */
export function isFieldRequired(field: FormFieldWithValues, ctx: BedRuleContext): boolean {
  if (!isFieldVisible(field, ctx)) return false;

  const role = roleKeyOf(field.target_role);

  // Rôle inactif (copilote absent) : rien n'est exigé tant que rien n'est saisi.
  if (role !== 'common' && !ctx.active.has(role)) return false;

  // Groupe reporté : le participant s'est engagé sur une date, on n'exige plus.
  if (field.bed_deferrable_group && isGroupDeferred(ctx, field.bed_deferrable_group, role)) {
    return false;
  }

  // Cahier L47 : « plus étudiant » coché → la profession devient obligatoire.
  if (ctx.index.profession[role]?.id === field.id) {
    return isNotStudent(ctx.values, ctx.index.student[role]?.id);
  }

  // Cahier L46 : la ville n'est exigée que si une école est saisie (elle sert à
  // la création de l'école, qui doit porter nom + ville).
  if (field.contact_field_map === 'school_city') {
    const schoolField = ctx.index.school[role];
    return Boolean(schoolField && isFilled(ctx.values, schoolField.id));
  }

  // « Si oui, quelle édition » : requis dès que « a déjà participé » est coché.
  if (ctx.index.previousEdition[role]?.id === field.id) return true;

  // Adresse de réception du courrier : bloc « tout ou rien ». Facultatif tant
  // qu'il est vide, entièrement requis dès qu'une ligne est renseignée.
  if (isMailAddressField(field)) {
    const block = ctx.index.mailAddress[role] ?? [];
    return block.some((f) => isFilled(ctx.values, f.id));
  }

  return field.is_required;
}

// -----------------------------------------------------------------------
// ÉLÉMENTS MANQUANTS
// -----------------------------------------------------------------------

export type BedMissingKind = 'deferred' | 'empty';

export interface BedMissingItem {
  /** `field:<uuid>` ou `<groupe>:<rôle>`. */
  code: string;
  kind: BedMissingKind;
  label: string;
  role: string;
  role_label: string;
  group?: string | null;
  due_date?: string | null;
  field_id?: string | null;
}

/**
 * Miroir client de `bed_compute_missing_items` (00177). Mêmes règles, même
 * ordre (reports d'abord, puis les champs vides dans l'ordre du formulaire).
 * Le serveur reste la référence pour ce qui est PERSISTÉ ; cette fonction
 * alimente l'affichage immédiat (frise, en-tête) entre deux enregistrements.
 */
export function computeMissingItems(
  fields: FormFieldWithValues[],
  values: BedValues,
  deferred: BedDeferralMap
): BedMissingItem[] {
  const ctx = buildRuleContext(fields, values, deferred);

  const deferredItems: BedMissingItem[] = Object.keys(deferred)
    .sort()
    .map((code) => {
      const [group, role] = code.split(':');
      return {
        code,
        kind: 'deferred' as const,
        group,
        role,
        label: BED_DEFERRAL_GROUP_LABELS[group as BedDeferralGroup] ?? group,
        role_label: BED_ROLE_LABELS[role] ?? role,
        due_date: deferred[code]?.due_date ?? null,
      };
    });

  const emptyItems: BedMissingItem[] = [...fields]
    .sort((a, b) => a.order_index - b.order_index)
    .filter((field) => isFieldRequired(field, ctx) && !isFilled(values, field.id))
    .map((field) => {
      const role = roleKeyOf(field.target_role);
      return {
        code: `field:${field.id}`,
        kind: 'empty' as const,
        group: field.bed_deferrable_group ?? null,
        role,
        label: field.label,
        role_label: BED_ROLE_LABELS[role] ?? role,
        field_id: field.id,
      };
    });

  return [...deferredItems, ...emptyItems];
}

// -----------------------------------------------------------------------
// ÉTAT DE LA FRISE
// -----------------------------------------------------------------------

/**
 * - `complete` : rien ne manque sur l'étape ;
 * - `warning`  : navigable (rien de bloquant) mais au moins un « je fournirai
 *                plus tard » — c'est le ⚠ de la frise ;
 * - `partial`  : commencée, il reste des champs requis vides ;
 * - `todo`     : pas encore touchée.
 */
export type BedStepState = 'complete' | 'warning' | 'partial' | 'todo';

export interface BedStepExtras {
  /** Étape ⑤ : une case « j'ai pris connaissance » manque-t-elle ? */
  optionsBlocked?: boolean;
  /** Étape ⑥ : signatures manquantes. */
  signaturesMissing?: boolean;
  /** Reports déclarés hors champs de formulaire (certificat médical). */
  extraDeferrals?: string[];
}

export function computeStepState(
  step: BedStepId,
  fieldsOfStep: FormFieldWithValues[],
  ctx: BedRuleContext,
  extras: BedStepExtras = {}
): BedStepState {
  const visible = fieldsOfStep.filter((f) => isFieldVisible(f, ctx));
  const required = visible.filter((f) => isFieldRequired(f, ctx));
  const missing = required.filter((f) => !isFilled(ctx.values, f.id));
  const touched = visible.some((f) => isFilled(ctx.values, f.id));

  // Reports rattachés à cette étape (groupes des champs qu'elle contient, plus
  // les reports hors formulaire déclarés par l'appelant — cf. certificat ⑥).
  const stepGroups = new Set<string>();
  for (const field of fieldsOfStep) {
    if (field.bed_deferrable_group) {
      stepGroups.add(deferralKey(field.bed_deferrable_group, roleKeyOf(field.target_role)));
    }
  }
  (extras.extraDeferrals ?? []).forEach((key) => stepGroups.add(key));
  const hasDeferral = [...stepGroups].some((key) => Boolean(ctx.deferred[key]));

  const blocked = Boolean(extras.optionsBlocked) || Boolean(extras.signaturesMissing);

  if (missing.length > 0 || blocked) {
    return touched || hasDeferral || blocked ? 'partial' : 'todo';
  }
  if (hasDeferral) return 'warning';
  if (!touched && visible.length > 0 && step !== 6) return 'todo';
  return 'complete';
}

/** Une étape est franchissable dès qu'elle n'a plus rien de bloquant. */
export function isStepSatisfied(state: BedStepState): boolean {
  return state === 'complete' || state === 'warning';
}

/**
 * Étape sur laquelle atterrir à la réouverture du lien (cahier L94) : la
 * première non satisfaite ; si tout est satisfait, le récapitulatif ⑥.
 */
export function landingStep(states: Record<BedStepId, BedStepState>): BedStepId {
  for (const step of BED_STEPS) {
    if (!isStepSatisfied(states[step.id])) return step.id;
  }
  return BED_LAST_STEP;
}

// -----------------------------------------------------------------------
// DOCUMENTS DE CONDITIONS (exigence assureur, cahier L91)
// -----------------------------------------------------------------------

/**
 * Une prise de connaissance ne vaut que pour la VERSION AFFICHÉE du document :
 * si l'assureur remplace le fichier, l'ancienne preuve ne compte plus. C'est la
 * règle exacte du socle 00166 (`conditions_url_snapshot = conditions_url`),
 * reproduite ici pour que l'UI verrouille avant même l'aller-retour serveur.
 * Un item sans document n'a rien à acquitter : il est acquitté par nature.
 */
export function isConditionsAcknowledged(
  item: { pricing_item_id: string; conditions_url: string | null },
  acknowledged: Record<string, string>
): boolean {
  if (!item.conditions_url) return true;
  return acknowledged[item.pricing_item_id] === item.conditions_url;
}

// -----------------------------------------------------------------------
// MONTANTS
// -----------------------------------------------------------------------

export interface BedAmountInput {
  /** Solde serveur (obligatoires − acompte − réduction), en centimes. */
  soldeCents: number;
  /** Somme des options facultatives cochées, en centimes. */
  optionsCents: number;
}

/** Total dû = solde serveur + options choisies. Jamais recalculé autrement. */
export function computeTotalCents({ soldeCents, optionsCents }: BedAmountInput): number {
  return soldeCents + optionsCents;
}

/**
 * Borne le montant que le participant choisit de régler maintenant : au moins
 * 1 centime, jamais plus que le restant dû (cahier : « il peut le diminuer
 * mais ne jamais mettre plus que le montant global restant à payer »).
 */
export function clampAmountToPay(requestedCents: number, totalCents: number): number {
  if (!Number.isFinite(requestedCents)) return totalCents;
  return Math.min(Math.max(Math.round(requestedCents), 1), totalCents);
}

/** Formatage monétaire FR à partir de centimes. */
export function formatCents(cents: number): string {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(cents / 100);
}
